import React from "react";
import { Banner, Button, FormField, Input } from "@/ds";
import { cockpit } from "@/api";
import type { InvitationPreview } from "@/api";
import { Wordmark } from "../brand";
import { LanguageSwitcher, useT } from "../i18n";
import { isThrottled, refusalKey, retryAfterMinutes, tokenFromHash } from "./invitation.logic";
import { NeoMailboxPicker, pickedAddress } from "./neo-mailbox-picker";
import type { PickerState } from "./neo-mailbox-picker";
import { WorkingAddressDialog } from "./working-address";
import styles from "./screen-invitation.module.css";

/**
 * Where an invited business owner lands (#780).
 *
 * Unauthenticated, and the last screen before they have an account — so it has to do three things
 * at once: prove the link is real, show them what we already filled in, and let them correct it.
 * Being told your own company's name is wrong and not being able to fix it is a bad first five
 * minutes, so every prepared field is editable.
 *
 * The token comes from the URL fragment rather than the path or query. Browsers do not send
 * fragments, so the credential never reaches an access log, a proxy, or the `Referer` of anything
 * this page loads. It is read once on mount and never put back into the URL.
 *
 * The address is chosen here and only here. #779 made it unchangeable from the tenant plane
 * afterwards, so this is the one moment the business decides what it is called — which is why the
 * step is in the registration form rather than somewhere in settings they might never open.
 *
 * A refusal says only that the link is not usable. It cannot say which of expired, withdrawn or
 * already-used applies, because that would confirm to anyone guessing that a link once existed.
 */

type Phase =
  | { kind: "loading" }
  | { kind: "invalid" }
  /**
   * The server asked this caller to wait (#1163). Its own phase, because it is the opposite of
   * "invalid": the link is fine, and the one thing the person must not do is throw it away.
   */
  | { kind: "throttled"; minutes: number }
  | { kind: "form"; preview: InvitationPreview }
  /**
   * Registered, but the address could not be issued. The only reason this state exists is that
   * the alternative is a redirect that carries the bad news away with it: the person would arrive
   * in the product believing mail is reaching them. They continue when they have read it.
   */
  | { kind: "mailboxFailed" }
  /**
   * Registered, with an address (#1229). The person is one click from the product and has never
   * seen the address they just chose in use; this is where it is explained, before the reload
   * takes them somewhere with a hundred other things on it.
   */
  | { kind: "welcome"; address: string };

export function InvitationScreen() {
  const t = useT();
  const [phase, setPhase] = React.useState<Phase>({ kind: "loading" });
  const [displayName, setDisplayName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [repeat, setRepeat] = React.useState("");
  const [localPart, setLocalPart] = React.useState("");
  const [picker, setPicker] = React.useState<PickerState>({ kind: "idle" });
  /**
   * Unticked by default and never ticked for them. The address cannot be changed afterwards, so
   * the product must not be able to say "you chose it" about something that was the default state
   * of a form somebody clicked through.
   */
  const [addressAcknowledged, setAddressAcknowledged] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const submissionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // Rejections arrive after the person has scrolled to submit. Keep the explanation and retry
    // action together in view; the remounted Banner announces the error without moving focus.
    if (error) submissionRef.current?.scrollIntoView({ block: "nearest", behavior: "instant" });
  }, [error]);

  // Captured once: the fragment is the only copy, and re-reading it later would break if
  // anything on the page ever navigates.
  const [token] = React.useState(() => tokenFromHash(window.location.hash));

  // Keep the switch available in every invitation phase, including invalid and loading links.
  const languageBar = (
    <>
      <div className="preauth__lang"><LanguageSwitcher light /></div>
      <div className={styles.languageBar}><Wordmark /></div>
    </>
  );

  // Stable across renders so the picker's debounce is not restarted by every keystroke elsewhere
  // in the form. The token is the whole authority for asking.
  const checkAddress = React.useCallback(
    (value: string) => cockpit.checkInvitationAddress(token, value),
    [token],
  );

  React.useEffect(() => {
    let live = true;
    void (async () => {
      if (!token) {
        setPhase({ kind: "invalid" });
        return;
      }
      try {
        const preview = await cockpit.previewInvitation(token);
        if (!live) return;
        setDisplayName(preview.displayName);
        setSlug(preview.slug);
        setName(preview.invitedName ?? "");
        setEmail(preview.invitedEmail ?? "");
        setPhase({ kind: "form", preview });
      } catch (e: unknown) {
        if (!live) return;
        setPhase(isThrottled(e) ? { kind: "throttled", minutes: retryAfterMinutes(e) } : { kind: "invalid" });
      }
    })();
    return () => {
      live = false;
    };
  }, [token]);

  const mismatch = password !== "" && repeat !== "" && password !== repeat;
  const offersMailbox = phase.kind === "form" && phase.preview.mailboxDomain !== null;
  // The address is optional — an owner can add one later — but a name that is typed and not yet
  // cleared must not be submitted, or they would land in the product without the address they
  // believe they chose. And a name that is settled still needs the permanence acknowledged.
  const addressWanted = offersMailbox && localPart.trim() !== "";
  const addressSettled =
    !addressWanted || (pickedAddress(picker) !== null && addressAcknowledged);
  const ready =
    displayName.trim() !== "" &&
    slug.trim() !== "" &&
    email.trim() !== "" &&
    password.length >= 10 &&
    password === repeat &&
    addressSettled;

  /**
   * Into the product. The session is already a cookie, and a full load drops the fragment from the
   * address bar on the way in, so the spent token does not sit in the browser's history entry for
   * the signed-in page.
   *
   * Getting started rather than the Inbox (#1286): this is the one moment we know for certain is a
   * business's first, and an empty Inbox answers none of the questions somebody has who has just
   * been handed software that reads their customers' mail. Only this handoff goes there — a later
   * sign-in lands where it always did, and the page stays in the navigation to be re-read.
   */
  const enter = () =>
    window.location.replace(`${import.meta.env.BASE_URL || "/"}settings/setup`);

  const submit = () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    cockpit
      .acceptInvitation({
        token,
        displayName: displayName.trim(),
        slug: slug.trim(),
        name: name.trim() || undefined,
        email: email.trim(),
        password,
        localPart: addressWanted ? localPart.trim() : undefined,
      })
      .then((result) => {
        // The account and the business exist either way; only the address may be missing.
        if (result.mailboxProvisioned === false) {
          setPhase({ kind: "mailboxFailed" });
          setBusy(false);
          return;
        }
        const address = addressWanted ? pickedAddress(picker) : null;
        if (address) {
          setPhase({ kind: "welcome", address });
          setBusy(false);
          return;
        }
        enter();
      })
      .catch((e: unknown) => {
        setError(refusalKey(e));
        setBusy(false);
      });
  };

  if (phase.kind === "welcome") {
    return (
      <div className={styles.page} data-testid="invitation-welcome">
        {languageBar}
        {/* Every way out of the dialog continues into the product: there is nothing to decide
            here, and the address is stated again in Settings. */}
        <WorkingAddressDialog open address={phase.address} hosted onClose={enter} />
      </div>
    );
  }

  if (phase.kind === "mailboxFailed") {
    return (
      <div className={styles.page} data-testid="invitation-mailbox-failed">
        {languageBar}
        <Banner tone="warning" title={t("invite.accept.mailboxFailedTitle")}>
          <p style={{ margin: 0 }}>{t("invite.accept.mailboxFailedBody")}</p>
        </Banner>
        <div style={{ marginTop: "var(--space-3)" }}>
          <Button variant="primary" onClick={enter}>
            {t("invite.accept.mailboxFailedContinue")}
          </Button>
        </div>
      </div>
    );
  }

  if (phase.kind === "loading") {
    return (
      <div className={styles.page}>
        {languageBar}
        <p className="subtle" role="status">{t("invite.accept.loading")}</p>
      </div>
    );
  }

  if (phase.kind === "throttled") {
    return (
      <div className={styles.page} data-testid="invitation-throttled">
        {languageBar}
        <div className={`page-head ${styles.header}`}>
          <h1>{t("invite.accept.contextTitle")}</h1>
        </div>
        <Banner tone="warning" title={t("invite.accept.throttledTitle")}>
          {t("invite.accept.throttledBody", { minutes: phase.minutes })}
        </Banner>
        <a className={styles.returnLink} href={`${import.meta.env.BASE_URL}login`}>
          {t("invite.accept.toLogin")}
        </a>
      </div>
    );
  }

  if (phase.kind === "invalid") {
    return (
      <div className={styles.page} data-testid="invitation-invalid">
        {languageBar}
        <div className={`page-head ${styles.header}`}>
          <h1>{t("invite.accept.contextTitle")}</h1>
        </div>
        <Banner tone="danger" title={t("invite.accept.invalidTitle")}>
          {t("invite.accept.invalidBody")}
        </Banner>
        <a className={styles.returnLink} href={`${import.meta.env.BASE_URL}login`}>
          {t("invite.accept.toLogin")}
        </a>
      </div>
    );
  }

  return (
    <div className={styles.page} data-testid="invitation-screen">
      {languageBar}
      <div className={`page-head ${styles.header}`}>
        <div>
          <h1>{t("invite.accept.title")}</h1>
          <p>{t("invite.accept.sub")}</p>
        </div>
      </div>
      <div className={styles.form}>
        <section className={styles.section} aria-labelledby="invitation-business-title">
          <h2 id="invitation-business-title">{t("invite.accept.businessTitle")}</h2>
          <FormField label={t("invite.accept.companyLabel")} htmlFor="invite-company">
            <Input
              id="invite-company"
              autoComplete="organization"
              value={displayName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)}
            />
          </FormField>
          <div className={styles.field}>
            <FormField label={t("invite.accept.slugLabel")} htmlFor="invite-slug">
              <Input
                id="invite-slug"
                autoComplete="off"
                value={slug}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSlug(e.target.value)}
              />
            </FormField>
            <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
              {t("invite.accept.slugHint")}
            </p>
          </div>
        </section>
        <section className={styles.section} aria-labelledby="invitation-access-title">
          <h2 id="invitation-access-title">{t("invite.accept.accessTitle")}</h2>
          <FormField label={t("invite.accept.nameLabel")} htmlFor="invite-name">
            <Input
              id="invite-name"
              autoComplete="name"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </FormField>
          <div className={styles.field}>
            <FormField label={t("invite.accept.emailLabel")} htmlFor="invite-email">
              <Input
                id="invite-email"
                autoComplete="email"
                type="email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              />
            </FormField>
            <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
              {t("invite.accept.emailHint")}
            </p>
          </div>

          <div className={styles.field}>
            <FormField label={t("invite.accept.passwordLabel")} htmlFor="invite-password">
              <Input
                id="invite-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              />
            </FormField>
            <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
              {t("invite.accept.passwordHint")}
            </p>
          </div>
          <div className={styles.field}>
            <FormField label={t("invite.accept.passwordRepeatLabel")} htmlFor="invite-repeat">
              <Input
                id="invite-repeat"
                type="password"
                autoComplete="new-password"
                value={repeat}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRepeat(e.target.value)}
              />
            </FormField>
            {mismatch && (
              <p style={{ margin: 0, fontSize: "var(--text-sm)" }}>
                {t("invite.accept.passwordMismatch")}
              </p>
            )}
          </div>
        </section>

        {offersMailbox && (
          <section className={styles.section} aria-labelledby="invitation-mailbox-title" data-testid="invitation-mailbox">
            <h2 id="invitation-mailbox-title">{t("invite.accept.mailboxTitle")}</h2>
            <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
              {t("invite.accept.mailboxHint")}
            </p>
            <NeoMailboxPicker
              localPart={localPart}
              onLocalPartChange={setLocalPart}
              onStateChange={setPicker}
              domain={phase.preview.mailboxDomain}
              inputId="invitation-local-part"
              check={checkAddress}
            />
            {/* Only once they have actually typed something. Somebody skipping the step should
                not be shown a confirmation for a decision they are not making. */}
            {addressWanted && (
              <>
                <label
                  style={{
                    display: "flex",
                    gap: "var(--space-2)",
                    alignItems: "flex-start",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={addressAcknowledged}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setAddressAcknowledged(e.target.checked)
                    }
                    data-testid="invitation-address-ack"
                  />
                  <span>{t("invite.accept.mailboxAck")}</span>
                </label>
                {!addressAcknowledged && (
                  <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
                    {t("invite.accept.mailboxAckRequired")}
                  </p>
                )}
              </>
            )}
          </section>
        )}

        <div ref={submissionRef} className={styles.submission}>
          {error && <Banner tone="danger" title={t("common.error")}>{t(error)}</Banner>}
          <div>
            <Button loading={busy} variant="primary" disabled={busy || !ready} aria-busy={busy} onClick={submit}>
              {t("invite.accept.submit")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
